//  Description: This component fetches and displays script reporting data in a modal. It allows users to start/stop scripts, edit intervals, and view running scripts and resource usage.
//
// It also includes a search feature and handles missing scripts.
//ScriptReporting.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Table, Modal, message, InputNumber, Button, Switch, Tooltip, Popconfirm, AutoComplete, Input } from 'antd';
import api from '../functions/api'; // Assuming this is a pre-configured Axios instance
import ScriptExecutionTimeline from './ScriptExecutionTimeline';
import CronScheduleHeatmap from './CronScheduleHeatmap';
import CronScheduleEditor from './CronScheduleEditor';

import { CopyOutlined } from '@ant-design/icons'; // Import Ant Design's copy icon
import { DeleteOutlined } from '@ant-design/icons';
import { Modal as MuiModal } from '@mui/material';
import { Tooltip as MuiTooltip } from '@mui/material';


const ScriptReporting = ({ isVisible, onClose }) => {
  const [data, setData] = useState([]);
  const [retryData, setRetryData] = useState({}); // DAS-2194: retry info by script_name
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [showRedAlertsOnly, setShowRedAlertsOnly] = useState(false);
  const countExceededInterval = () => data.filter(isRedAlert).length;
  const [scriptData, setScriptData] = useState({}); // Add state for script_data
  const isEditing = (record) => record.ID === editingKey;
  const [logFile, setLogFile] = useState(''); // State for log file
  const [missingScripts, setMissingScripts] = useState([]); // State for missing_in_db
  const [showMissingScripts, setShowMissingScripts] = useState(false); // Toggle visibility
  const [runningScripts, setRunningScripts] = useState([]);
const [resourceUsage, setResourceUsage] = useState({});
// DAS-2432: default the Running Scripts panel to visible (was hidden behind
// the "Show Running Scripts and Resource Usage" toggle). The toggle remains
// for users who want to collapse the panel; the button text flips to
// "Hide …" when the panel is open. fetchRunningScripts() already runs on
// mount independently (see line ~287) so the initial render shows fresh data
// without an extra fetch.
const [isRunningScriptsVisible, setIsRunningScriptsVisible] = useState(true);
const [loadingRunningScripts, setLoadingRunningScripts] = useState(false);
const [filteredData, setFilteredData] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchText, setSearchText] = useState(''); // State to store the search text
  const [isErrorAssessmentVisible, setIsErrorAssessmentVisible] = useState(false);
const [errorAssessmentContent, setErrorAssessmentContent] = useState('');
const [scriptExecVisible, setScriptExecVisible] = useState(false);
const [scriptExecData, setScriptExecData] = useState([]);
const [loadingExecData, setLoadingExecData] = useState(false);
const [scriptErrors, setScriptErrors] = useState([]);
const [selectedError, setSelectedError] = useState(null);
const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
const diskCritical = Number(resourceUsage['Disk Usage']) >= 90;
const assessmentPollRef = useRef(null);
const [killingChromium, setKillingChromium] = useState(false);
const [sudoModalVisible, setSudoModalVisible] = useState(false);
const [sudoPassword, setSudoPassword] = useState('');
const [sudoError, setSudoError] = useState('');
const [cronHeatmapVisible, setCronHeatmapVisible] = useState(false);
const [cronEditorVisible, setCronEditorVisible] = useState(false);
const [cronEditorScript, setCronEditorScript] = useState(null);
const [cronEditorSchedule, setCronEditorSchedule] = useState('');



useEffect(() => {
  if (isVisible) {
    fetchRunningScripts();
    fetchData();

    // Preload error assessments
    api.get('/cron_scripts_assessment')
      .then(res => {
        if (Array.isArray(res.data)) setScriptErrors(res.data);
      })
      .catch(() => setScriptErrors([]));
  }
}, [isVisible]);


const formatAssessment = (text) => {
  if (!text) return '';

  const escapeHTML = (str) =>
    str.replace(/&/g, '&amp;')
       .replace(/</g, '&lt;')
       .replace(/>/g, '&gt;');

  const lines = text.split('\n');
  let html = '';
  const anchors = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Build clickable list at top
    if (line.startsWith('- ')) {
      const script = line.slice(2).trim();
      anchors.push(`<a href="#${script}">${script}</a>`);
    }

    // Detect script block
    if (line.startsWith('SCRIPT: ')) {
      const script = line.slice(8).trim();
      html += `<br><br><a id="${script}"></a><b>SCRIPT: ${script}</b><br>`;
    } else {
      html += escapeHTML(line) + '<br>';
    }
  }

  return `<div style="margin-bottom:10px;"><b>Jump to:</b><br>${anchors.join(' | ')}</div><hr>${html}`;
};


const handleClearLogfile = async () => {
  try {
    const response = await api.post('/clear_logfile');
    if (response.data?.status === 'success') {
      message.success('Logfile cleared successfully.');
    } else {
      message.error('Failed to clear logfile.');
    }
  } catch (error) {
    message.error('Error clearing logfile.');
    console.error(error);
  }
};


const fetchErrorAssessment = async () => {
  try {
    const response = await api.get('/cron_scripts_assessment');
    if (Array.isArray(response.data)) {
      setScriptErrors(response.data);
      setIsErrorAssessmentVisible(true);
    } else {
      message.error('No error assessments found.');
    }
  } catch (error) {
    message.error('Failed to fetch error assessments.');
    console.error('API error:', error);
  }
};

const getMaxAssessmentEpoch = (items) => {
  if (!Array.isArray(items) || items.length === 0) return 0;
  return items.reduce((max, it) => Math.max(max, Number(it.epoch || 0)), 0);
};

const pollAssessmentResults = (prevMax, timeoutMs = 60000, intervalMs = 5000) => {
  if (assessmentPollRef.current) {
    clearInterval(assessmentPollRef.current);
    assessmentPollRef.current = null;
  }
  const startedAt = Date.now();
  assessmentPollRef.current = setInterval(async () => {
    try {
      const res = await api.get('/cron_scripts_assessment');
      const data = Array.isArray(res.data) ? res.data : [];
      const newMax = getMaxAssessmentEpoch(data);
      if (newMax > prevMax) {
        setScriptErrors(data);
        setIsErrorAssessmentVisible(true);
        message.success('New Script Error Assessment results are available.');
        clearInterval(assessmentPollRef.current);
        assessmentPollRef.current = null;
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        message.warning('No new results detected yet; the assessment may still be running.');
        clearInterval(assessmentPollRef.current);
        assessmentPollRef.current = null;
      }
    } catch (e) {
      // Keep polling; optionally log
      console.error('Polling error:', e);
    }
  }, intervalMs);
};

const rerunErrorAssessment = async () => {
  const prevMax = getMaxAssessmentEpoch(scriptErrors);
  try {
    const response = await api.post('/scripts', { action: 'execute', script_name: 'DR_CRONscripts_daily_error_assessment.py' });
    if (response.status === 200 && response.data) {
      const { message: backendMessage, pid, log_file, command } = response.data || {};
      const successMsg = backendMessage || 'Started DR_CRONscripts_daily_error_assessment.py';
      message.success(`${successMsg}${pid ? ` (PID: ${pid})` : ''}${log_file ? ` - Log: ${log_file}` : ''}`, 6);
      message.info('Monitoring for new Script Error Assessment results…');
      pollAssessmentResults(prevMax);
    } else {
      throw new Error(response.data?.error || 'Unknown error starting assessment');
    }
  } catch (e) {
    console.error('Error starting assessment:', e);
    message.error(`Failed to start Script Error Assessment: ${e.message}`);
  }
};


const fetchScriptExecutions = async () => {
  setLoadingExecData(true);
  try {
    const res = await api.post('/get_script_execution', { limit: 50 });

    console.log("Response from script execution", res);

    if (Array.isArray(res.data)) setScriptExecData(res.data);
  } catch (err) {
    message.error('Failed to fetch script execution data');
    console.error(err);
  } finally {
    setLoadingExecData(false);
    setScriptExecVisible(true);
  }
};

const fetchRunningScripts = async () => {
  setLoadingRunningScripts(true);
  try {
    const response = await api.post('/get_runningscripts'); // Fetch data
    // console.log("response running scripts", response)
    const { running_scripts, resource_usage } = response.data;
    setRunningScripts(running_scripts);
    setResourceUsage(resource_usage);
  } catch (error) {
    message.error('Failed to fetch running scripts and resource usage.');
    console.error('API error:', error);
  } finally {
    setLoadingRunningScripts(false);
  }
};

const killChromiumProcesses = async (withSudo = false) => {
  setKillingChromium(true);
  setSudoError('');
  
  try {
    const payload = withSudo && sudoPassword ? { sudo_password: sudoPassword } : {};
    const response = await api.post('/kill_chromium_processes', payload);
    const data = response.data;
    
    if (data.success) {
      if (data.killed > 0) {
        message.success(`Killed ${data.killed} chromium processes`);
      }
      
      // Check if sudo password was wrong
      if (data.sudo_error) {
        setSudoError(data.sudo_error);
        message.error(data.sudo_error);
      } else if (data.needs_sudo && data.remaining_count > 0) {
        // Processes remain that need sudo - show password modal
        const users = Object.keys(data.remaining_users || {}).join(', ');
        message.info(`${data.remaining_count} process(es) owned by ${users} need sudo to kill`);
        setSudoModalVisible(true);
      } else if (data.after_count === 0) {
        // All killed
        message.success('All chromium processes killed');
        setSudoModalVisible(false);
        setSudoPassword('');
      }
      
      // Refresh the resource usage
      fetchRunningScripts();
    } else {
      message.error(data.message || 'Failed to kill chromium processes');
    }
  } catch (error) {
    message.error('Failed to kill chromium processes');
    console.error('Kill chromium error:', error);
  } finally {
    setKillingChromium(false);
  }
};

const handleSudoKill = async () => {
  if (!sudoPassword) {
    setSudoError('Please enter sudo password');
    return;
  }
  await killChromiumProcesses(true);
};

const closeSudoModal = () => {
  setSudoModalVisible(false);
  setSudoPassword('');
  setSudoError('');
};

useEffect(() => {
  if (isVisible) {
    fetchData(); // Fetch data for the table
    fetchRunningScripts(); // Fetch running scripts upon rendering
  }
}, [isVisible]);

useEffect(() => {
  return () => {
    if (assessmentPollRef.current) {
      clearInterval(assessmentPollRef.current);
      assessmentPollRef.current = null;
    }
  };
}, []);
const isScriptRunning = (scriptName) => {
  return runningScripts.some((script) => {
    // Normalize paths and extract script name
    const runningScriptName = script.Script.split('/').pop(); // Extract the file name
    return runningScriptName === scriptName; // Compare file names
  });
};

const stopScript = async (pid) => {
  try {
    await api.post('/scripts', { action: 'stop', PID: pid }); // Include the action parameter
    message.success(`Script with PID ${pid} stopped successfully!`);
    fetchRunningScripts(); // Refresh the running scripts list
  } catch (error) {
    message.error(`Failed to stop script with PID ${pid}.`);
    console.error('API error:', error);
  }
};

const [scriptStartLoading, setScriptStartLoading] = useState(false); // Spinner state for starting scripts

const startScript = async (scriptName) => {
  setScriptStartLoading(true); // Start the spinner for the "Start Script" button
  try {
    // console.log(`Sending request to start script: ${scriptName}`);
    const response = await api.post('/scripts', { action: 'execute', script_name: scriptName });

    // Check response structure
    if (response.status === 200 && response.data) {
      const { message: backendMessage, pid, log_file, command } = response.data || {}; // Safely access properties
      if (backendMessage) {
        // Enhanced success message with cron details
        const successMsg = `${backendMessage}${pid ? ` (PID: ${pid})` : ''}${log_file ? ` - Check logs at: ${log_file}` : ''}`;
        message.success(successMsg, 6); // Show for 6 seconds to read log path
        
        // Log the cron command for debugging
        if (command) {
          console.log('🔧 Cron command executed:', command);
        }
      } else {
        throw new Error('Backend did not return a success message.');
      }
      fetchRunningScripts(); // Refresh running scripts
    } else {
      throw new Error(response.data?.error || 'Unknown error occurred'); // Handle unexpected responses
    }
  } catch (error) {
    console.error('Error while starting script:', error);
    message.error(`Failed to start script "${scriptName}": ${error.message}`);
  } finally {
    setScriptStartLoading(false); // Stop the spinner
  }
};


const runningScriptsColumns = [
  {
    title: 'Action',
    key: 'action',
    render: (_, record) => (
      <Popconfirm
        title={`Are you sure you want to stop the script with PID ${record.PID}?`}
        onConfirm={() => stopScript(record.PID)} // Execute the stop action on confirmation
        okText="Yes"
        cancelText="No"
      >
        <Button
          type="danger"
          size="small"
          style={{
            fontSize: '10px',
          }}
        >
          Stop Script
        </Button>
      </Popconfirm>
    ),
  },
  
  {
    title: 'PID',
    dataIndex: 'PID',
    key: 'PID',
  },
  {
    title: 'User',
    dataIndex: 'User',
    key: 'User',
  },
  {
    title: 'Script',
    dataIndex: 'Script',
    key: 'Script',
  },
  {
    title: 'Command',
    dataIndex: 'Command',
    key: 'Command',
    render: (text) => (
      <Tooltip title={text}>
        <span style={{ fontSize: '10px', textDecoration: 'underline', color: '#1890ff' }}>
          {text.length > 30 ? `${text.slice(0, 30)}...` : text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'CPU Usage (%)',
    dataIndex: 'CPU Usage (%)',
    key: 'CPU Usage (%)',
  },
  {
    title: 'Memory Usage (MB)',
    dataIndex: 'Memory Usage (MB)',
    key: 'Memory Usage (MB)',
  },
  // DAS-2392: single column showing process start time, elapsed time so far,
  // and the previous run's duration (from script_reporting.duration).
  {
    title: 'Timing',
    key: 'Timing',
    render: (_, record) => {
      const fmtClock = (epoch) => {
        if (epoch == null) return '—';
        const d = new Date(epoch * 1000);
        const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
        const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        return `${date} ${time}`;
      };
      const fmtDur = (secs) => {
        if (secs == null) return '—';
        const s = Math.max(0, Math.round(secs));
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const r = s % 60;
        if (h > 0) return `${h}h ${m}m ${r}s`;
        if (m > 0) return `${m}m ${r}s`;
        return `${r}s`;
      };
      return (
        <div style={{ fontSize: '10px', lineHeight: '1.4' }}>
          <div>Start: {fmtClock(record.start_epoch)}</div>
          <div>Elapsed: {fmtDur(record.elapsed_seconds)}</div>
          <div>Prev run: {fmtDur(record.previous_duration_seconds)}</div>
        </div>
      );
    },
  },
];


  const handleDelete = async (record) => {
    try {
      await api.delete('/script_reporting', { data: { ID: record.ID } }); // DELETE request
      message.success(`Script "${record.script_name}" deleted successfully!`);
      fetchData(); // Refreshes table data
    } catch (error) {
      message.error('Failed to delete script.');
      console.error('API error:', error);
    }
  };
  
  

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success('Copied to clipboard!');
      })
      .catch(() => {
        message.error('Failed to copy!');
      });
  };

  const parseCronInterval = (cron) => {
    if (!cron) return null;
    const parts = cron.split(' ');
    if (parts.length < 5) return null;
  
    const [minute, hour, day, month, dayOfWeek] = parts;
  
    // Daily interval
    if (minute !== '*' && hour !== '*' && day === '*' && month === '*' && dayOfWeek === '*') {
      return 24; // 24 hours
    }
  
    // Weekly interval (runs on a specific day of the week)
    if (dayOfWeek !== '*' && day === '*' && month === '*') {
      return 7 * 24; // 7 days * 24 hours = 168 hours
    }
  
    // Default: return null if not recognized
    return null;
  };
  

  const handleSearch = (value) => {
    setSearchText(value); // Track search input
  };
  


  const handleSelect = (value) => {
    const selected = data.filter((item) => item.script_name === value);
    setFilteredData(selected); // Update filtered data with selected item
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/script_reporting');
      // console.log("Response from script reporting", response);
  
      const { database_results, script_data, retry_archive, retry_pending } = response.data;

      setData(database_results); // Set table data
      setFilteredData(database_results); // Initialize filtered data
      setSearchOptions(
        database_results.map((item) => ({
          value: item.script_name, // Use script name for suggestions
        }))
      );
      setScriptData(script_data || {}); // Populate scriptData with script_data

      // DAS-2194: Build retry lookup by script_name (most recent entry per script)
      const retryMap = {};
      (retry_archive || []).forEach((r) => {
        if (!retryMap[r.script_name] || new Date(r.archived_at) > new Date(retryMap[r.script_name].archived_at)) {
          retryMap[r.script_name] = { ...r, source: 'archive' };
        }
      });
      (retry_pending || []).forEach((r) => {
        retryMap[r.script_name] = { ...r, source: 'pending' };
      });
      setRetryData(retryMap);
    } catch (error) {
      message.error('Failed to fetch script reporting data.');
      console.error('API error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  

  

  useEffect(() => {
    if (isVisible) {
      fetchData(); // Fetch data whenever the modal becomes visible
    }
  }, [isVisible]);

  const edit = (record) => {
    setEditingKey(record.ID);
  };

  const save = async (id, newInterval) => {
    try {
      await api.put('/script_reporting', { ID: id, interval: newInterval });
      message.success('Interval updated successfully!');
      fetchData(); // Refresh data after update
    } catch (error) {
      message.error('Failed to update interval.');
      console.error('API error:', error);
    } finally {
      setEditingKey('');
    }
  };

  const cancel = () => {
    setEditingKey('');
  };

  // DAS-2196: Detect skipped scripts (e.g., weekly scripts on non-run days)
  const isSkipped = (record) => {
    return record.essential_results && record.essential_results.toLowerCase().includes('status: skipped');
  };

  const getRowStyle = (record) => {
    // DAS-2196: Skipped scripts get a neutral style, not a red alert
    if (isSkipped(record)) {
      return {
        border: '1px solid #d9d9d9',
        padding: '4px',
        borderRadius: '4px',
        opacity: 0.7,
      };
    }

    const currentTime = Date.now();
    const recordTime = record.epoch * 1000; // Convert epoch to milliseconds
    const intervalInMillis = record.interval * 60 * 60 * 1000; // Convert interval to milliseconds

    if (currentTime - recordTime > intervalInMillis) {
      return {
        border: '1px solid red',
        padding: '4px',
        borderRadius: '4px',
      };
    }

    return {};
  };

  const isRedAlert = (record) => {
    // DAS-2196: Skipped scripts are not red alerts
    if (isSkipped(record)) return false;
    const currentTime = Date.now();
    const recordTime = record.epoch * 1000; // Convert epoch to milliseconds
    const intervalInMillis = record.interval * 60 * 60 * 1000; // Convert interval to milliseconds
    return currentTime - recordTime > intervalInMillis;
  };

  const removePrefixAndSuffix = (scriptName) => {
    return scriptName.replace(/^(DR_|DC_|DS_)/, '').replace(/\.py$/, '');
  };

  const finalFilteredData = (showRedAlertsOnly ? data.filter(isRedAlert) : data).filter((item) =>
  item.script_name.toLowerCase().includes(searchText.toLowerCase())
);

  
  // const filteredData = showRedAlertsOnly ? data.filter(isRedAlert) : data;

  const columns = [
    
    {
  title: 'ID',
  dataIndex: 'ID',
  key: 'ID',
  sorter: (a, b) => a.ID - b.ID,
  render: (text, record) => {
    const hasOrphaned = record.orphaned_processes && record.orphaned_processes.trim() !== '';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {hasOrphaned && (
          <Tooltip title={record.orphaned_processes}>
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                backgroundColor: 'red',
                borderRadius: '50%',
              }}
            />
          </Tooltip>
        )}
        <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>{text}</span>
      </div>
    );
  },
},

    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const isRunning = isScriptRunning(record.script_name); // Check if the script is running
        return (
          <Popconfirm
            title={`Are you sure you want to start "${record.script_name}"?`}
            onConfirm={() => startScript(record.script_name)}
            okText="Yes"
            cancelText="No"
            disabled={isRunning} // Disable Popconfirm if running
          >
            <Button
              type="primary"
              size="small"
              disabled={isRunning || scriptStartLoading} // Disable button if running or loading
              style={{ fontSize: '10px' }}
            >
              {isRunning ? 'Running' : 'Start Script'}
            </Button>
          </Popconfirm>
        );
      },
    },
    

    {
      title: 'Script Name',
      dataIndex: 'script_name',
      key: 'script_name',
      sorter: (a, b) => a.script_name.localeCompare(b.script_name),
      render: (_, record) => {
        const copyToClipboard = (text) => {
          navigator.clipboard
            .writeText(text)
            .then(() => {
              message.success('Copied to clipboard!');
            })
            .catch(() => {
              message.error('Failed to copy!');
            });
        };
    
        const isRed = isRedAlert(record);
        const isRunning = isScriptRunning(record.script_name);
    
        const parentName = record.parent_script;
        const parentSchedule = parentName ? (scriptData[parentName] || '') : '';

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isRunning && (
              <span
                style={{
                  height: '10px',
                  width: '10px',
                  backgroundColor: 'red',
                  borderRadius: '50%',
                  display: 'inline-block',
                  marginRight: '8px',
                }}
              ></span>
            )}
            <div style={parentName ? { lineHeight: 'normal' } : undefined}>
              <span
                style={{
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '10px',
                  fontWeight: isRed ? 'bold' : 'normal',
                  color: isRed ? 'red' : 'inherit',
                }}  
              >
                {record.script_name}
              </span>
              {parentName && (
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '9px', color: '#888', marginTop: '1px' }}>
                  [parent: {parentName}{parentSchedule ? ` ${parentSchedule}` : ''}]
                </div>
              )}
            </div>
            <CopyOutlined
              onClick={() => copyToClipboard(record.script_name)}
              style={{
                marginLeft: 8,
                cursor: 'pointer',
                color: '#1890ff',
              }}
              title="Copy to clipboard"
            />
          </div>
        );
      },
    },
    
    
    
    {
      title: 'Name',
      key: 'name',
      sorter: (a, b) => {
        const nameA = removePrefixAndSuffix(a.script_name).toLowerCase();
        const nameB = removePrefixAndSuffix(b.script_name).toLowerCase();
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>{removePrefixAndSuffix(record.script_name)}</span>
          <Popconfirm
  title="Are you sure you want to delete this script from the script reporting table?"
  onConfirm={() => handleDelete(record)} // Executes delete logic
  okText="Yes"
  cancelText="No"
>
  <Button
    type="text"
    icon={<DeleteOutlined style={{ color: 'red' }} />}
    size="small"
  />
</Popconfirm>

        </div>
      ),
    },
    {
      title: 'Cron Schedule',
      key: 'cron_schedule',
      sorter: (a, b) => {
        const cronA = scriptData[a.script_name] || '';
        const cronB = scriptData[b.script_name] || '';
        return cronA.localeCompare(cronB);
      },
      render: (_, record) => {
        const cronSchedule = scriptData[record.script_name] || '-';
        return (
          <div
            style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', cursor: cronSchedule !== '-' ? 'pointer' : 'default', color: cronSchedule !== '-' ? '#1890ff' : undefined }}
            onClick={() => {
              if (cronSchedule !== '-') {
                setCronEditorScript(record.script_name);
                setCronEditorSchedule(cronSchedule);
                setCronEditorVisible(true);
              }
            }}
          >
            {cronSchedule}
          </div>
        );
      },
    },
    
    
    {
      title: 'Interval',
      dataIndex: 'interval',
      key: 'interval',
      sorter: (a, b) => a.interval - b.interval,
      editable: true,
      render: (_, record) => {
        const editable = isEditing(record);
        const cronSchedule = scriptData[record.script_name] || '';
        const expectedInterval = parseCronInterval(cronSchedule);
        const isMismatch = expectedInterval && record.interval !== expectedInterval;
    
        return editable ? (
          <InputNumber
            min={0}
            defaultValue={record.interval}
            onBlur={(e) => save(record.ID, e.target.value)}
            style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}
          />
        ) : (
          <span
            onClick={() => edit(record)}
            style={{
              cursor: 'pointer',
              color: isMismatch ? 'red' : 'blue',
              fontFamily: 'Arial, sans-serif',
              fontSize: '10px',
            }}
          >
            {record.interval || '-'}
            {isMismatch && <span style={{ color: 'red', marginLeft: '4px' }}>?</span>}
          </span>
        );
      },
    },
    
    {
      title: 'Duration (minutes)',
      dataIndex: 'duration',
      key: 'duration',
      sorter: (a, b) => a.duration - b.duration,
      render: (text) => (
        <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>{text}</span>
      ),
    },
   
    {
      title: 'Essential Results',
      dataIndex: 'essential_results',
      key: 'essential_results',
      sorter: (a, b) => (a.essential_results || '').localeCompare(b.essential_results || ''),
      render: (text) => {
        if (text) {
          // DAS-2196: Show "Skipped" label for skipped scripts
          const skipped = text.toLowerCase().includes('status: skipped');
          return (
            <Tooltip title={text}>
              <span
                style={{
                  color: skipped ? '#faad14' : '#1890ff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '10px',
                  fontWeight: skipped ? 500 : 'normal',
                }}
              >
                {skipped ? 'Skipped' : 'Tooltip'}
              </span>
            </Tooltip>
          );
        }
        return (
          <span
            style={{
              fontFamily: 'Arial, sans-serif',
              fontSize: '10px',
            }}
          >
            -
          </span>
        );
      },
    },
    {
      title: 'Epoch',
      dataIndex: 'epoch',
      key: 'epoch',
      sorter: (a, b) => a.epoch - b.epoch,
      render: (text, record) => {
        // Check if script is not running and latest start > latest finish
        const isSuspicious = record.epoch_latest_script_start &&
                           record.epoch_latest_script_start > record.epoch;
        // DAS-2194: Retry info for this script
        const retry = retryData[record.script_name];

        return (
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>
            <span style={{ whiteSpace: 'nowrap' }}>
              {isSuspicious && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: 'red',
                    borderRadius: '50%',
                    marginRight: '6px',
                    verticalAlign: 'middle'
                  }}
                  title="Suspicious: Latest start is later than latest finish"
                />
              )}
              {new Date(text * 1000).toLocaleString()}
            </span>
            {retry && (
              <div style={{
                marginTop: '3px',
                fontSize: '9px',
                padding: '2px 5px',
                borderRadius: '3px',
                backgroundColor: retry.source === 'pending' ? '#fff7e6' :
                  retry.status === 'completed' ? '#f6ffed' : '#fff1f0',
                border: `1px solid ${retry.source === 'pending' ? '#ffd591' :
                  retry.status === 'completed' ? '#b7eb8f' : '#ffa39e'}`,
                whiteSpace: 'nowrap',
              }}>
                {retry.source === 'pending' ? '⏳ Retry pending' :
                  retry.status === 'completed' ? '🔄 Retried ✓' :
                  '🔄 Retry failed'}
                {retry.retry_finished_at && (
                  <span style={{ marginLeft: '4px', color: '#888' }}>
                    {new Date(retry.retry_finished_at).toLocaleString()}
                  </span>
                )}
                {retry.killed_at && retry.source === 'pending' && (
                  <span style={{ marginLeft: '4px', color: '#888' }}>
                    killed {new Date(retry.killed_at).toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
  title: 'Latest Script Start',
  dataIndex: 'epoch_latest_script_start',
  key: 'epoch_latest_script_start',
  sorter: (a, b) => (a.epoch_latest_script_start || 0) - (b.epoch_latest_script_start || 0),
  render: (value) => {
    if (!value) return (
      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>
        {/* Empty if null or zero */}
      </span>
    );

    return (
      <span
        style={{
          fontFamily: 'Arial, sans-serif',
          fontSize: '10px',
          whiteSpace: 'nowrap',
        }}
      >
        {new Date(value * 1000).toLocaleString()}
      </span>
    );
  }
},

  ];

  return (
  <>
    <Modal
      title={
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            
            <span>
  Script Reporting (
  {countExceededInterval()} scripts exceeded interval
  {data.filter(d => d.orphaned_processes && d.orphaned_processes.trim() !== "").length > 0 && (
    <span style={{ color: 'red' }}>
      , {data.filter(d => d.orphaned_processes && d.orphaned_processes.trim() !== "").length} scripts with orphaned processes
    </span>
  )}
  )
</span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button 
                type="primary" 
                size="small"
                onClick={() => {
                  fetchData();
                  fetchRunningScripts();
                }} 
                loading={loading}
              >
                Refresh
              </Button>
              <Switch
                checked={showRedAlertsOnly}
                onChange={setShowRedAlertsOnly}
                checkedChildren="Red Alerts On"
                unCheckedChildren="Red Alerts Off"
              />
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            <Button
  type="link"
  onClick={() => {
    setIsRunningScriptsVisible(!isRunningScriptsVisible);
    if (!isRunningScriptsVisible) fetchRunningScripts();
  }}
>
  {diskCritical && (
    <span
      style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        backgroundColor: 'red',
        borderRadius: '50%',
        marginRight: '6px',
      }}
    />
  )}
  {isRunningScriptsVisible
    ? 'Hide Running Scripts and Resource Usage'
    : 'Show Running Scripts and Resource Usage'}
</Button>


            {/* New position for Show Execution Timeline button */}
            <Button
              type="link"
              onClick={fetchScriptExecutions}
              loading={loadingExecData}
              style={{ marginLeft: 0, marginTop: 4 }}
            >
              Show Execution Timeline
            </Button>
            <Button
              type="link"
              onClick={() => setCronHeatmapVisible(true)}
              style={{ marginLeft: 8, marginTop: 4 }}
            >
              Show Cron Heatmap
            </Button>

            <div style={{ marginTop: '12px' }}>
              <Button
  type="link"
  onClick={fetchErrorAssessment}
  disabled={scriptErrors.length === 0}
>
  <span style={{ display: 'flex', alignItems: 'center' }}>
    Show Script Error Assessment
    {scriptErrors.length > 0 && (
      <span
        style={{
          backgroundColor: 'red',
          color: 'white',
          borderRadius: '50%',
          padding: '0 6px',
          fontSize: '10px',
          marginLeft: '6px',
          display: 'inline-block',
          minWidth: '16px',
          textAlign: 'center',
        }}
      >
        {scriptErrors.length}
      </span>
    )}
  </span>
</Button>
              <Button
                type="link"
                onClick={rerunErrorAssessment}
                style={{ marginLeft: 8 }}
              >
                Re-run Error Assessment
              </Button>

            </div>

            {isRunningScriptsVisible && (
              <div style={{ marginTop: '8px' }}>
                {loadingRunningScripts ? (
                  <p>Loading...</p>
                ) : (
                  <>
                    <h4>Running Scripts</h4>
                    <Table
                      dataSource={runningScripts}
                      columns={runningScriptsColumns}
                      rowKey="PID"
                      pagination={false}
                      size="small"
                    />
                    <h4>Resource Usage</h4>
<p style={{ fontSize: '10px' }}>
  CPU Usage: {resourceUsage['CPU Usage'] ?? '-'}%<br />
  Memory Usage: {resourceUsage['Memory Usage'] ?? '-'}%<br />
  Disk Usage: {resourceUsage['Disk Usage'] ?? '-'}%<br />
  <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
    Chromium Processes: {resourceUsage['Chromium Processes'] ?? '-'}
    {(resourceUsage['Chromium Processes'] ?? 0) > 0 && (
      <Popconfirm
        title="Kill all chromium processes?"
        description="This will terminate all chromium-browser processes on the server."
        onConfirm={() => killChromiumProcesses(false)}
        okText="Yes, kill them"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <Button 
          type="link" 
          danger 
          size="small" 
          loading={killingChromium}
          style={{ marginLeft: '8px', padding: '0 4px', fontSize: '10px' }}
        >
          Kill All
        </Button>
      </Popconfirm>
    )}
  </span>
</p>

                  </>
                )}
              </div>
            )}
          </div>

          {/* Log File Section */}
          {logFile && (
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
                Logfile: {logFile}
              </span>
              <CopyOutlined
                onClick={() => copyToClipboard(logFile)}
                style={{
                  marginLeft: '8px',
                  cursor: 'pointer',
                  color: '#1890ff',
                }}
                title="Copy log file path"
              />
            </div>
          )}
          {/* Missing Scripts Section */}
          {missingScripts.length > 0 && (
            <div style={{ marginTop: '8px', fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
              <span
                onClick={() => setShowMissingScripts(!showMissingScripts)}
                style={{
                  color: '#1890ff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Scripts not in reporting database
              </span>
              {showMissingScripts && (
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  {missingScripts.map((script, index) => (
                    <li key={index} style={{ fontSize: '10px' }}>{script}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      }
      visible={isVisible}
      onCancel={onClose}
      footer={[
        <Button key="refresh" onClick={() => {
          fetchData();
          fetchRunningScripts();
        }} loading={loading}>
          Refresh
        </Button>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>
        // No Show Execution Timeline here anymore
      ]}
      width={1500}
    >
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <AutoComplete
          style={{ width: 300 }}
          options={searchOptions}
          placeholder="Search script by name"
          onSearch={handleSearch}
          onSelect={handleSelect}
          allowClear
        />
        <Button 
          type="primary" 
          onClick={() => {
            fetchData();
            fetchRunningScripts();
          }} 
          loading={loading}
        >
          Refresh
        </Button>
      </div>
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
  <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
    /home/hansmoonen/pyscripts/crontabcopy/crontab/logfile.log
  </span>
  <CopyOutlined
    onClick={() => copyToClipboard('/home/hansmoonen/pyscripts/crontabcopy/crontab/logfile.log')}
    style={{
      marginLeft: '8px',
      cursor: 'pointer',
      color: '#1890ff',
    }}
    title="Copy log file path"
  />
  <Popconfirm
    title="Are you sure you want to empty the logfile?"
    onConfirm={handleClearLogfile}
    okText="Yes"
    cancelText="No"
  >
    <Button type="primary" size="small" danger style={{ marginLeft: '32px' }}>
      Empty Log
    </Button>
  </Popconfirm>
</div>


      <Table
        dataSource={finalFilteredData}
        columns={columns}
        rowKey="ID"
        loading={loading}
        pagination={{ pageSize: 20 }}
        rowClassName={() => 'table-low-row'}
      />
    </Modal>

    <Modal
  title="Script Error Assessment"
  visible={isErrorAssessmentVisible}
  onCancel={() => setIsErrorAssessmentVisible(false)}
  footer={[
    <Button key="rerun_assessment" type="primary" onClick={rerunErrorAssessment}>
      Re-run Error Assessment
    </Button>,
    <Button key="close" onClick={() => setIsErrorAssessmentVisible(false)}>
      Close
    </Button>
  ]}
  width={800}
>
  <Table
    dataSource={scriptErrors}
    rowKey="ID"
    pagination={false}
    columns={[
      {
          title: 'Script Name',
          dataIndex: 'script_name',
          key: 'script_name',
          render: (text, record) => (
            <span
              style={{ fontSize: '12px', fontFamily: 'Arial, sans-serif', color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                setSelectedError(record);
                setIsDetailModalVisible(true);
              }}
            >
              {text}
            </span>
          )
        },

      {
        title: 'Timestamp',
        dataIndex: 'epoch',
        key: 'epoch',
        render: (value) =>
          value ? (
            <span style={{ fontSize: '12px', fontFamily: 'Arial, sans-serif' }}>
              {new Date(value * 1000).toLocaleString()}
            </span>
          ) : (
            '-'
          )
      }
    ]}
  />
</Modal>

<Modal
  visible={isDetailModalVisible}
  onCancel={() => setIsDetailModalVisible(false)}
  footer={[
    <Button
      key="jira"
      type="primary"
      onClick={async () => {
        if (!selectedError) return;
        try {
          const resp = await api.post('/script_error_ticket', {
            script_name: selectedError.script_name,
            error_assessment: selectedError.error_assessment || '',
            script_error: selectedError.script_error || ''
          });
          if (resp?.data?.success) {
            message.success(`Jira ticket created: ${resp.data.key}`);
          } else if (resp?.data?.key) {
            message.success(`Jira ticket created: ${resp.data.key}`);
          } else {
            message.error(resp?.data?.error || 'Failed to create Jira ticket');
          }
        } catch (e) {
          message.error('Failed to create Jira ticket');
          console.error(e);
        }
      }}
    >
      Create Jira Ticket
    </Button>,
    <Button
      key="rerun_assessment_detail"
      onClick={rerunErrorAssessment}
    >
      Re-run Error Assessment
    </Button>,
    <Button
      key="rerun"
      onClick={() => {
        if (selectedError?.script_name) {
          startScript(selectedError.script_name);
        }
      }}
    >
      Re-run Script
    </Button>,
    <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
      Close
    </Button>
  ]}
  title={`Script Error: ${selectedError?.script_name || ''}`}
  width={900}
>
  {selectedError && (
    <div style={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre-wrap' }}>
      <p><strong>Script:</strong> {selectedError.script_name}</p>
      <p><strong>Date:</strong> {new Date(selectedError.epoch * 1000).toLocaleString()}</p>
      <p><strong>Assessment:</strong></p>
      <div style={{ backgroundColor: '#f9f9f9', padding: '10px', border: '1px solid #ccc', marginBottom: '16px' }}>
        {selectedError.error_assessment || '(none)'}
      </div>
      <p><strong>Error Traceback:</strong></p>
      <div style={{ backgroundColor: '#f0f0f0', padding: '10px', border: '1px solid #ccc', overflowX: 'auto' }}>
        {selectedError.script_error || '(none)'}
      </div>
    </div>
  )}
</Modal>



    {/* Sudo Password Modal for killing root-owned chromium processes */}
    <Modal
      title="Sudo Password Required"
      open={sudoModalVisible}
      onCancel={closeSudoModal}
      footer={[
        <Button key="cancel" onClick={closeSudoModal}>
          Cancel
        </Button>,
        <Button 
          key="kill" 
          type="primary" 
          danger 
          loading={killingChromium}
          onClick={handleSudoKill}
        >
          Kill Processes
        </Button>
      ]}
    >
      <p style={{ marginBottom: '12px' }}>
        Some chromium processes are owned by other users and require sudo to kill.
      </p>
      <Input.Password
        placeholder="Enter sudo password"
        value={sudoPassword}
        onChange={(e) => setSudoPassword(e.target.value)}
        onPressEnter={handleSudoKill}
        status={sudoError ? 'error' : ''}
      />
      {sudoError && (
        <p style={{ color: 'red', marginTop: '8px', fontSize: '12px' }}>
          {sudoError}
        </p>
      )}
    </Modal>

    <MuiModal open={scriptExecVisible} onClose={() => setScriptExecVisible(false)}>
      <ScriptExecutionTimeline
        scriptExecVisible={scriptExecVisible}
        setScriptExecVisible={setScriptExecVisible}
        scriptExecData={scriptExecData}
      />
    </MuiModal>

    <CronScheduleHeatmap
      visible={cronHeatmapVisible}
      onClose={() => setCronHeatmapVisible(false)}
    />

    <CronScheduleEditor
      visible={cronEditorVisible}
      onClose={() => setCronEditorVisible(false)}
      scriptName={cronEditorScript}
      currentSchedule={cronEditorSchedule}
      onSuccess={() => fetchData()}
    />
  </>
);

  
};

export default ScriptReporting;
